import React, { useEffect, useState, useCallback } from 'react';
import { adminCarBenefitsService } from '../../services/adminService';

const CarBenefitsForm = ({ carId }) => {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ 
    title: '', 
    description: '', 
    included: true 
  });
  const [editingId, setEditingId] = useState(null);

  // Use useCallback to memoize the function
  const loadBenefits = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminCarBenefitsService.getBenefits(carId);
      setBenefits(res.benefits || []);
    } catch (error) {
      console.error('Error loading benefits:', error);
      alert('Failed to load benefits');
    } finally {
      setLoading(false);
    }
  }, [carId]); // carId as dependency

  useEffect(() => {
    if (carId) {
      loadBenefits();
    }
  }, [carId, loadBenefits]); // Add loadBenefits to dependencies

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert('Benefit title is required');
      return;
    }

    try {
      if (editingId) {
        await adminCarBenefitsService.updateBenefit(carId, editingId, form);
        alert('Benefit updated successfully!');
      } else {
        await adminCarBenefitsService.createBenefit(carId, form);
        alert('Benefit added successfully!');
      }
      setForm({ title: '', description: '', included: true });
      setEditingId(null);
      loadBenefits();
    } catch (error) {
      alert('Failed to save benefit: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (benefit) => {
    setForm({
      title: benefit.title,
      description: benefit.description,
      included: benefit.included
    });
    setEditingId(benefit.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this benefit?')) return;
    
    try {
      await adminCarBenefitsService.deleteBenefit(carId, id);
      alert('Benefit deleted successfully!');
      loadBenefits();
    } catch (error) {
      alert('Failed to delete benefit: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleCancel = () => {
    setForm({ title: '', description: '', included: true });
    setEditingId(null);
  };

  if (loading) {
    return (
      <div className="car-benefits-form">
        <h3>💎 Car Benefits</h3>
        <div className="loading-state">Loading benefits...</div>
      </div>
    );
  }

  return (
    <div className="car-benefits-form">
      <div className="form-header">
        <h3>💎 Car Benefits</h3>
        <span className="benefit-count">{benefits.length} benefits</span>
      </div>

      <form onSubmit={handleSubmit} className="benefit-form">
        <div className="form-row">
          <div className="form-group">
            <label>Benefit Title *</label>
            <input
              type="text"
              placeholder="e.g., Extended Warranty, Free Service"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
            />
          </div>

          <div className="form-group full-width">
            <label>Description *</label>
            <textarea
              placeholder="Describe the benefit in detail..."
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows="3"
              required
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                checked={form.included}
                onChange={(e) => setForm({ ...form, included: e.target.checked })}
              />
              Included
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary">
            {editingId ? '✏️ Update Benefit' : '➕ Add Benefit'}
          </button>
          {editingId && (
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="benefits-list">
        {benefits.length === 0 ? (
          <div className="empty-state">
            <p>No benefits added yet. Add your first benefit above!</p>
          </div>
        ) : (
          <div className="benefits-grid">
            {benefits.map(benefit => (
              <div key={benefit.id} className={`benefit-card ${!benefit.included ? 'not-included' : ''}`}>
                <div className="benefit-header">
                  <h4>{benefit.included ? '✅' : '❌'} {benefit.title}</h4>
                  <div className="benefit-actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(benefit)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(benefit.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarBenefitsForm;